import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Upload,
  Image as ImageIcon,
  FolderOpen,
  X,
  Loader2,
} from "lucide-react";
import MediaPickerModal from "./MediaPickerModal";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://ckgo2-api.martin-94d.workers.dev";

interface Props {
  value?: string;
  onChange: (url: string) => void;
  folder?: string;
  label?: string;
  placeholder?: string;
  className?: string;
}

export default function ImagePicker({
  value,
  onChange,
  folder = "uploads",
  label,
  placeholder = "Vyberte nebo nahrajte obrázek",
  className = "",
}: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);

      const response = await fetch(`${API_BASE}/api/admin/upload`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Upload failed");
      }

      return response.json();
    },
    onSuccess: (result) => {
      if (result.data?.url) {
        onChange(result.data.url);
        queryClient.invalidateQueries({ queryKey: ["media-files"] });
      }
      setUploadError(null);
    },
    onError: (error) => {
      setUploadError(error instanceof Error ? error.message : "Upload failed");
    },
  });

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle drag & drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      uploadMutation.mutate(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className={className}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      {uploadError && (
        <div className="mb-2 rounded-lg bg-error-50 p-2 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
          {uploadError}
        </div>
      )}

      <div
        className="relative overflow-hidden rounded-lg border-2 border-dashed border-gray-300 transition-colors hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {value ? (
          // Image preview
          <div className="relative aspect-video bg-gray-100 dark:bg-gray-800">
            <img
              src={value}
              alt="Preview"
              className="h-full w-full object-cover"
            />
            {/* Overlay with actions */}
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 transition-all hover:bg-black/50 hover:opacity-100">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-gray-700 shadow hover:bg-white"
              >
                <Upload className="mr-1 inline-block h-4 w-4" />
                Změnit
              </button>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="rounded-lg bg-white/90 px-3 py-2 text-sm font-medium text-gray-700 shadow hover:bg-white"
              >
                <FolderOpen className="mr-1 inline-block h-4 w-4" />
                Knihovna
              </button>
              <button
                type="button"
                onClick={() => onChange("")}
                className="rounded-lg bg-error-500/90 px-3 py-2 text-sm font-medium text-white shadow hover:bg-error-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          // Empty state
          <div className="flex aspect-video flex-col items-center justify-center p-6 text-center">
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="mb-2 h-10 w-10 animate-spin text-primary-500" />
                <p className="text-sm text-gray-500">Nahrávám...</p>
              </>
            ) : (
              <>
                <ImageIcon className="mb-2 h-10 w-10 text-gray-400" />
                <p className="text-sm text-gray-500">{placeholder}</p>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-ghost text-sm"
                  >
                    <Upload className="mr-1 h-4 w-4" />
                    Nahrát
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="btn-ghost text-sm"
                  >
                    <FolderOpen className="mr-1 h-4 w-4" />
                    Z knihovny
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Media picker modal */}
      <MediaPickerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={(url) => {
          onChange(url);
          setIsModalOpen(false);
        }}
      />
    </div>
  );
}
